import { useEffect, useRef, useState, useCallback } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { api } from '../api';
import type { HistoryAuditEntry } from '../types';

interface Props {
  domainId: string;
  domain: string;
  tier: string;
  verified: boolean;
}

function ScoreBadge({ score }: { score: number }) {
  const color = score >= 80 ? 'text-green-600 bg-green-50' : score >= 60 ? 'text-yellow-600 bg-yellow-50' : 'text-red-600 bg-red-50';
  return (
    <span className={`inline-block px-2 py-0.5 rounded text-sm font-semibold ${color}`}>
      {score}/100
    </span>
  );
}

// Auto-poll interval when no audits exist yet — 15 s, max 5 min
const POLL_INTERVAL_MS = 15_000;
const POLL_MAX_MS = 5 * 60 * 1000;

export function AnalyticsTab({ domainId, domain, tier, verified }: Props) {
  const [audits, setAudits] = useState<HistoryAuditEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [polling, setPolling] = useState(false);
  const [reauditing, setReauditing] = useState(false);
  const [reauditMsg, setReauditMsg] = useState('');
  const pollStartRef = useRef<number | null>(null);
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const handleReaudit = useCallback(async () => {
    setReauditing(true);
    setReauditMsg('');
    try {
      await api.domains.reaudit(domainId);
      setReauditMsg('Audit queued — results will appear here in ~2 minutes.');
      setPolling(true);
      pollStartRef.current = Date.now();
    } catch {
      setReauditMsg('Failed to queue audit — please try again.');
    } finally {
      setReauditing(false);
    }
  }, [domainId]);

  function stopPolling() {
    if (pollTimerRef.current !== null) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
    setPolling(false);
    pollStartRef.current = null;
  }

  useEffect(() => {
    let cancelled = false;

    async function fetchAudits() {
      try {
        const res = await api.domains.getAudits(domainId);
        if (cancelled) return;
        setAudits(res.audits);
        if (res.audits.length > 0) {
          stopPolling();
        }
      } catch (err: unknown) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load audit history');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchAudits();

    return () => { cancelled = true; };
  }, [domainId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Start polling when verified + no audits (audit may be in progress)
  useEffect(() => {
    if (!verified || audits.length > 0 || loading || error) {
      stopPolling();
      return;
    }
    if (pollTimerRef.current !== null) return; // already polling

    pollStartRef.current = Date.now();
    setPolling(true);

    pollTimerRef.current = setInterval(async () => {
      if (pollStartRef.current !== null && Date.now() - pollStartRef.current > POLL_MAX_MS) {
        stopPolling();
        return;
      }
      try {
        const res = await api.domains.getAudits(domainId);
        if (res.audits.length > 0) {
          setAudits(res.audits);
          stopPolling();
        }
      } catch {
        // non-fatal — keep polling
      }
    }, POLL_INTERVAL_MS);

    return () => { stopPolling(); };
  }, [verified, audits.length, loading, error, domainId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Use currentScores (rescored at latest methodology) wholesale when available.
  // Per-pillar fallback was causing zero legitimate scores to be overridden by
  // stale originalScores (e.g. cs.aeo=0 was replaced by os.aeo=15/80).
  const resolveScores = (a: HistoryAuditEntry) => {
    const cs = a.currentScores;
    const os = a.originalScores;
    const seo = cs !== null ? cs.seo : os.seo;
    const aeo = cs !== null ? cs.aeo : os.aeo;
    const geo = cs !== null ? cs.geo : os.geo;
    return { overall: (seo + aeo + geo) / 3, seo, aeo, geo };
  };

  // audits is already oldest→newest (ORDER BY audited_at ASC from API)
  const chartData = audits.map((a) => {
      const s = resolveScores(a);
      return {
        date: new Date(a.auditedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }),
        'AI Visibility': Math.round(s.overall),
        SEO:     Math.round(s.seo),
        AEO:     Math.round(s.aeo),
        GEO:     Math.round(s.geo),
      };
    });

  // audits is ASC — newest entry is last
  const latest = audits[audits.length - 1];
  const previous = audits[audits.length - 2];
  const latestScores  = latest   ? resolveScores(latest)   : null;
  const previousScores = previous ? resolveScores(previous) : null;
  const delta = latestScores && previousScores
    ? Math.round(latestScores.overall - previousScores.overall)
    : null;

  if (loading) return <div className="py-10 text-center text-gray-400 text-sm">Loading…</div>;
  if (error) return <div className="py-10 text-center text-red-500 text-sm">{error}</div>;

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-brand-ink">{domain}</h2>
          <p className="text-sm text-gray-400 mt-0.5">AI readiness score history</p>
        </div>
        {verified && (
          <div className="flex flex-col items-end gap-1">
            <button
              onClick={() => void handleReaudit()}
              disabled={reauditing || polling}
              className="text-sm font-medium px-4 py-2 bg-brand-ink text-white rounded-lg hover:bg-gray-800 disabled:opacity-50 transition-colors"
            >
              {reauditing ? 'Queuing…' : polling ? 'Audit running…' : 'Re-Audit'}
            </button>
            {reauditMsg && (
              <p className="text-xs text-gray-500">{reauditMsg}</p>
            )}
          </div>
        )}
      </div>

      {audits.length === 0 ? (
        <div className="text-center py-16">
          {!verified ? (
            <p className="text-gray-400 text-sm">Verify your domain to trigger your first audit.</p>
          ) : polling ? (
            <div className="space-y-3">
              <div className="inline-flex items-center gap-2 text-sm text-brand-green">
                <span className="inline-block w-3 h-3 rounded-full border-2 border-brand-green border-t-transparent animate-spin" />
                Audit in progress — checking for results…
              </div>
              <p className="text-xs text-gray-400">This usually takes 1–2 minutes.</p>
            </div>
          ) : (
            <p className="text-gray-400 text-sm">No audits yet — run your first audit from the Overview tab.</p>
          )}
        </div>
      ) : (
        <>
          {/* Score summary */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {([
              { label: 'AI Visibility Score', value: latestScores?.overall ?? null, delta },
              { label: 'SEO',     value: latestScores?.seo     ?? null, delta: null },
              { label: 'AEO',     value: latestScores?.aeo     ?? null, delta: null },
              { label: 'GEO',     value: latestScores?.geo     ?? null, delta: null },
            ] as { label: string; value: number | null; delta: number | null }[]).map(({ label, value, delta: d }) => (
              <div key={label} className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm">
                <p className="text-xs text-gray-400 mb-2">{label}</p>
                {value !== null
                  ? <ScoreBadge score={Math.round(value)} />
                  : <span className="text-sm text-gray-400">—</span>}
                {d !== null && (
                  <p className={`text-xs mt-1.5 ${d > 0 ? 'text-green-600' : d < 0 ? 'text-red-500' : 'text-gray-400'}`}>
                    {d > 0 ? '+' : ''}{d} since last
                  </p>
                )}
              </div>
            ))}
          </div>

          {/* Chart */}
          {chartData.length >= 2 ? (
            <div className="bg-white rounded-xl border border-gray-100 p-6 shadow-sm">
              <p className="text-sm font-medium text-brand-ink mb-4">Score over time</p>
              <ResponsiveContainer width="100%" height={240}>
                <LineChart data={chartData} margin={{ top: 5, right: 20, left: -10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#9ca3af' }} />
                  <YAxis domain={[0, 100]} tick={{ fontSize: 11, fill: '#9ca3af' }} />
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e5e7eb' }} />
                  <Legend wrapperStyle={{ fontSize: 11, paddingTop: 12 }} />
                  <Line type="monotone" dataKey="AI Visibility" stroke="#1e2d3d" strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                  <Line type="monotone" dataKey="SEO"     stroke="#22c55e" strokeWidth={1.5} dot={{ r: 2 }} activeDot={{ r: 4 }} strokeDasharray="0" />
                  <Line type="monotone" dataKey="AEO"     stroke="#3b82f6" strokeWidth={1.5} dot={{ r: 2 }} activeDot={{ r: 4 }} />
                  <Line type="monotone" dataKey="GEO"     stroke="#f59e0b" strokeWidth={1.5} dot={{ r: 2 }} activeDot={{ r: 4 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-gray-100 p-6 shadow-sm text-center">
              <p className="text-sm text-gray-400">Run at least 2 audits to see the score trend.</p>
            </div>
          )}

          {/* Audit history table */}
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="px-5 py-3 border-b border-gray-50">
              <p className="text-sm font-medium text-brand-ink">Audit history</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-50">
                    <th className="text-left px-5 py-2.5 text-xs text-gray-400 font-medium">Date</th>
                    <th className="text-left px-5 py-2.5 text-xs text-gray-400 font-medium">Overall</th>
                    <th className="text-left px-5 py-2.5 text-xs text-gray-400 font-medium">SEO</th>
                    <th className="text-left px-5 py-2.5 text-xs text-gray-400 font-medium">AEO</th>
                    <th className="text-left px-5 py-2.5 text-xs text-gray-400 font-medium">GEO</th>
                    <th className="text-left px-5 py-2.5 text-xs text-gray-400 font-medium">Change</th>
                  </tr>
                </thead>
                <tbody>
                  {[...audits].reverse().map((a, i, arr) => {
                    const prev = arr[i + 1];
                    const s = resolveScores(a);
                    const prevS = prev ? resolveScores(prev) : null;
                    const change = prevS ? Math.round(s.overall - prevS.overall) : null;
                    return (
                      <tr key={a.auditId} className="border-b border-gray-50 last:border-0">
                        <td className="px-5 py-3 text-gray-600 whitespace-nowrap">
                          {new Date(a.auditedAt).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' })}
                        </td>
                        <td className="px-5 py-3">
                          <ScoreBadge score={Math.round(s.overall)} />
                        </td>
                        <td className="px-5 py-3 text-gray-600">{Math.round(s.seo)}</td>
                        <td className="px-5 py-3 text-gray-600">{Math.round(s.aeo)}</td>
                        <td className="px-5 py-3 text-gray-600">{Math.round(s.geo)}</td>
                        <td className="px-5 py-3">
                          {change !== null ? (
                            <span className={change > 0 ? 'text-green-600' : change < 0 ? 'text-red-500' : 'text-gray-400'}>
                              {change > 0 ? '+' : ''}{change}
                            </span>
                          ) : <span className="text-gray-300">—</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Upgrade prompt for free users */}
          {tier === 'free' && (
            <div className="bg-brand-ink rounded-xl p-6 text-center">
              <p className="text-white font-medium mb-1">Want to improve your score?</p>
              <p className="text-white/50 text-sm mb-4">Upgrade to unlock AI suggestions, content editing, and GEO visibility tracking.</p>
              <button
                onClick={async () => {
                  const { url } = await api.billing.checkout('tier_1');
                  window.location.href = url;
                }}
                className="bg-brand-green text-white font-medium px-6 py-2.5 rounded-lg text-sm hover:bg-brand-green-dark transition-colors"
              >
                Upgrade to Starter — $29/mo
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
